import React from "react";
import {
  Box,
  Header,
  Popover,
  SpaceBetween,
  StatusIndicator,
  Steps,
} from "@amzn/awsui-components-react";
import {
  AccountingEventsGroup,
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
  HealthStatus,
  RedriveStatus,
} from "../../types";
import healthIndicator, {
  redriveHealthIndicator,
} from "../../config/healthIndicator";
import { FlowSteps } from "../../hooks/useHealthStatus";
import { getFlowDisplayName } from "../../helpers/flowTypeUtils";
import { CanonicalBusinessEvent } from "../../types/cbes";

interface HealthStatusIndicatorProps {
  healthStatus: HealthStatus | RedriveStatus;
  popoverContent?: React.ReactNode;
  flowSteps?: FlowSteps;
  minimal?: boolean;
  isLoading?: boolean;
  eventData?: (
    | AccountingEventsGroup
    | DigitalBusinessDocument
    | DigitalCostDocument
    | DigitalPaymentDocument
  )[];
  cbeData?: CanonicalBusinessEvent[];
}

const HealthStatusIndicator: React.FC<HealthStatusIndicatorProps> = ({
  healthStatus,
  popoverContent,
  flowSteps,
  minimal = false,
  isLoading = false,
  eventData = [],
  cbeData = [],
}) => {
  if (isLoading) {
    return (
      <Box display="inline-block">
        <StatusIndicator type="loading">Loading</StatusIndicator>
      </Box>
    );
  }

  const { type, color, message } =
    redriveHealthIndicator[healthStatus] ||
    healthIndicator[healthStatus] ||
    healthIndicator.unknown;

  const statusIndicatorContent = (
    <StatusIndicator type={type}>
      {minimal
        ? null
        : healthStatus.charAt(0).toUpperCase() + healthStatus.slice(1)}
    </StatusIndicator>
  );

  const content = flowSteps
    ? (() => {
        const showDocumentFlow =
          !flowSteps.hideDocumentFlow && flowSteps.documentFlow.length > 0;
        const showAccountingFlow =
          !flowSteps.hideAccountingFlow && flowSteps.accountingFlow.length > 0;
        const showHeaders = showDocumentFlow && showAccountingFlow;

        return (
          <SpaceBetween size="m">
            {showAccountingFlow && (
              <div>
                {showHeaders && (
                  <Header variant="h3">
                    {getFlowDisplayName("accounting", eventData, cbeData)}
                  </Header>
                )}
                <Steps steps={flowSteps.accountingFlow} />
              </div>
            )}

            {showDocumentFlow && (
              <div>
                {showHeaders && (
                  <Header variant="h3">
                    {getFlowDisplayName("document", eventData, cbeData)}
                  </Header>
                )}
                <Steps steps={flowSteps.documentFlow} />
              </div>
            )}

            <div>
              <Header variant="h3">DLQ Status</Header>
              <Steps steps={[flowSteps.dlqCheck]} />
            </div>
          </SpaceBetween>
        );
      })()
    : popoverContent;

  return (
    <Box color={color}>
      {minimal || (!popoverContent && !flowSteps) ? (
        statusIndicatorContent
      ) : (
        <Popover header={message} content={content}>
          {statusIndicatorContent}
        </Popover>
      )}
    </Box>
  );
};

export default HealthStatusIndicator;
